import React, { useState } from 'react';
import { Edit, Copy, Trash2, ChevronUp, ChevronDown, AlertCircle, Download } from 'lucide-react';
import { Pipeline, PipelineStatus } from '../../types/pipeline';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

interface PipelineTableProps {
  pipelines: Pipeline[];
  sortBy: 'name' | 'created_at' | 'status' | 'workflow_count';
  sortOrder: 'asc' | 'desc';
  onSort: (column: 'name' | 'created_at' | 'status' | 'workflow_count') => void;
  onEdit: (pipeline: Pipeline) => void;
  onDuplicate: (pipeline: Pipeline) => void;
  onExport: (pipeline: Pipeline) => void;
  onDelete: (pipeline: Pipeline) => void;
}

export const PipelineTable: React.FC<PipelineTableProps> = ({
  pipelines,
  sortBy,
  sortOrder,
  onSort,
  onEdit,
  onDuplicate,
  onExport,
  onDelete,
}) => {
  const [pipelineToDelete, setPipelineToDelete] = useState<Pipeline | null>(null);

  const getStatusColor = (status: PipelineStatus): string => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'DISABLED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'ERROR':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const SortIcon: React.FC<{ column: 'name' | 'created_at' | 'status' | 'workflow_count' }> = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const handleDeleteClick = (pipeline: Pipeline) => {
    setPipelineToDelete(pipeline);
  };

  const handleConfirmDelete = () => {
    if (pipelineToDelete) {
      onDelete(pipelineToDelete);
      setPipelineToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setPipelineToDelete(null);
  };

  const isDefaultPipeline = (pipeline: Pipeline): boolean => {
    return pipeline.name === 'Default Pipeline';
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 w-2/5"
                onClick={() => onSort('name')}
                data-testid="sort-name"
              >
                Name
                <SortIcon column="name" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => onSort('status')}
                data-testid="sort-status"
              >
                Status
                <SortIcon column="status" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Version
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Created By
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => onSort('created_at')}
                data-testid="sort-created"
              >
                Created Date
                <SortIcon column="created_at" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => onSort('workflow_count')}
                data-testid="sort-workflows"
              >
                Workflows
                <SortIcon column="workflow_count" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {pipelines.map((pipeline) => (
              <tr
                key={pipeline.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                data-testid={`pipeline-row-${pipeline.id}`}
              >
                <td className="px-6 py-4 max-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {pipeline.name}
                  </div>
                  {pipeline.description && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {pipeline.description}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pipeline.status)}`}
                    data-testid={`status-badge-${pipeline.id}`}
                  >
                    {pipeline.status === 'ERROR' && (
                      <AlertCircle className="w-3 h-3 mr-1" />
                    )}
                    {pipeline.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {pipeline.version}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {pipeline.created_by || 'Unknown'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(pipeline.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <span data-testid={`workflow-count-${pipeline.id}`}>
                    {pipeline.workflow_count}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEdit(pipeline)}
                      disabled={isDefaultPipeline(pipeline)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-blue-600 dark:disabled:hover:text-blue-400"
                      title={isDefaultPipeline(pipeline) ? "Cannot edit default pipeline" : "Edit pipeline"}
                      data-testid={`edit-button-${pipeline.id}`}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDuplicate(pipeline)}
                      className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                      title="Duplicate pipeline"
                      data-testid={`duplicate-button-${pipeline.id}`}
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onExport(pipeline)}
                      className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                      title="Export pipeline"
                      data-testid={`export-button-${pipeline.id}`}
                    >
                      <Download className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(pipeline)}
                      disabled={isDefaultPipeline(pipeline)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-red-600 dark:disabled:hover:text-red-400"
                      title={isDefaultPipeline(pipeline) ? "Cannot delete default pipeline" : "Delete pipeline"}
                      data-testid={`delete-button-${pipeline.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {pipelineToDelete && (
        <DeleteConfirmationModal
          pipeline={pipelineToDelete}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </>
  );
};

